import { useState, useRef, useEffect } from "react";
import {
    Box,
    Heading,
    VStack,
    HStack,
    Text,
    Button,
    Grid,
    Icon,
} from "@chakra-ui/react";
import { FaCheckCircle, FaCode, FaQuestionCircle } from "react-icons/fa";
import { useColorModeValue } from "../ui/color-mode";
import { useAccessibility } from "../../context/AccessibilityContext";
import navigationSound from "../../assets/sounds/navigation.mp3";

/**
 * QuizTypeSelectionScreen - Allows users to choose between:
 * 1. Multiple Choice Quiz (Selection-based questions)
 * 2. Coding Challenge (Code-based questions)
 * 
 * Designed with accessibility in mind for blind users
 */
export const QuizTypeSelectionScreen = ({ courseId = "python", onSelectQuizType, onBack }) => {
    const courseName = courseId === "javascript" ? "JavaScript" : courseId === "react" ? "React" : "Python";
    const [selectedType, setSelectedType] = useState(null);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const { soundEnabled } = useAccessibility();

    const selectionQuizRef = useRef(null);
    const codingQuizRef = useRef(null);
    const containerRef = useRef(null);

    const cardBg = useColorModeValue("white", "gray.800");
    const headingColor = useColorModeValue("gray.700", "white");
    const textColor = useColorModeValue("gray.600", "gray.400");
    const borderColor = useColorModeValue("gray.200", "gray.600");
    const hoverBg = useColorModeValue("blue.50", "blue.900");
    const selectedBg = useColorModeValue("blue.100", "blue.800");
    const screenBg = useColorModeValue("gray.50", "gray.900");

    useEffect(() => {
        // Auto-focus the container on mount
        if (containerRef.current) {
            containerRef.current.focus();
        }
    }, []);

    const playSound = (sound) => {
        if (soundEnabled) {
            new Audio(sound).play();
        }
    };

    const quizTypes = [
        {
            id: "selection",
            title: "Multiple Choice Quiz",
            description: "Answer questions by selecting from multiple options. Navigate with arrow keys and select with Enter.",
            icon: FaQuestionCircle,
            features: [
                "15 questions per module",
                "4 options per question",
                "Instant feedback",
                "Audio cues for correct/incorrect answers",
                "Full keyboard navigation"
            ],
            ref: selectionQuizRef,
            ariaLabel: "Multiple Choice Quiz. Navigate through questions and select answers from multiple options."
        },
        {
            id: "coding",
            title: "Coding Challenge",
            description: `Write actual ${courseName} code to solve programming tasks. Type code in an accessible editor with helpful keyboard shortcuts.`,
            icon: FaCode,
            features: [
                "Real coding exercises",
                "Accessible code editor",
                "Keyboard shortcuts (Ctrl+Enter to run, Ctrl+L for line number, Ctrl+H for hint)",
                "Detailed hints and descriptions",
                "Code validation and feedback"
            ],
            ref: codingQuizRef,
            ariaLabel: `Coding Challenge. Write and execute ${courseName} code to solve programming tasks.`
        }
    ];

    const handleKeyDown = (e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
            e.preventDefault();
            const nextIndex = focusedIndex === 0 ? 1 : 0;
            setFocusedIndex(nextIndex);
            setSelectedType(quizTypes[nextIndex].id);
            quizTypes[nextIndex].ref.current?.focus();
            playSound(navigationSound);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
            e.preventDefault();
            const prevIndex = focusedIndex === 1 ? 0 : 1;
            setFocusedIndex(prevIndex);
            setSelectedType(quizTypes[prevIndex].id);
            quizTypes[prevIndex].ref.current?.focus();
            playSound(navigationSound);
        } else if (e.key === "Enter" && selectedType) {
            e.preventDefault();
            handleContinue();
        }
    };

    const handleCardClick = (typeId, index) => {
        setSelectedType(typeId);
        setFocusedIndex(index);
        playSound(navigationSound);
    };

    const handleContinue = () => {
        if (selectedType) {
            playSound(navigationSound);
            onSelectQuizType(selectedType);
        }
    };

    return (
        <Box
            p={{ base: 4, md: 8 }}
            bg={screenBg}
            minHeight="100vh"
            width="100%"
        >
            <VStack spacing={8} maxW="1200px" mx="auto">
                {/* Header */}
                <Box textAlign="center" width="100%">
                    <Heading
                        size="2xl"
                        color={headingColor}
                        mb={4}
                        id="quiz-type-heading"
                    >
                        Choose Your Learning Path
                    </Heading>
                    <Text fontSize="lg" color={textColor} maxW="800px" mx="auto">
                        Select how you'd like to test your {courseName} knowledge.
                        Use arrow keys to navigate between options and Enter to select.
                    </Text>
                </Box>

                {/* Quiz Type Cards */}
                <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={8}
                    width="100%"
                    ref={containerRef}
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    role="radiogroup"
                    aria-labelledby="quiz-type-heading"
                    aria-activedescendant={selectedType}
                >
                    {quizTypes.map((type, index) => (
                        <Box
                            key={type.id}
                            ref={type.ref}
                            onClick={() => handleCardClick(type.id, index)}
                            onFocus={() => {
                                setSelectedType(type.id);
                                setFocusedIndex(index);
                            }}
                            tabIndex={0}
                            role="radio"
                            aria-checked={selectedType === type.id}
                            aria-label={type.ariaLabel}
                            p={8}
                            bg={selectedType === type.id ? selectedBg : cardBg}
                            borderRadius="xl"
                            borderWidth="3px"
                            borderColor={selectedType === type.id ? "blue.500" : borderColor}
                            boxShadow={selectedType === type.id ? "xl" : "md"}
                            cursor="pointer"
                            transition="all 0.3s ease"
                            _hover={{
                                bg: selectedType === type.id ? selectedBg : hoverBg,
                                transform: "translateY(-4px)",
                                boxShadow: "xl",
                            }}
                            _focus={{
                                outline: "none",
                                borderColor: "blue.500",
                                boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.5)",
                            }}
                        >
                            <VStack spacing={6} align="stretch">
                                {/* Icon and Title */}
                                <HStack spacing={4}>
                                    <Icon
                                        as={type.icon}
                                        boxSize={10}
                                        color="blue.500"
                                        aria-hidden="true"
                                    />
                                    <Heading size="lg" color={headingColor}>
                                        {type.title}
                                    </Heading>
                                    {selectedType === type.id && (
                                        <Icon
                                            as={FaCheckCircle}
                                            boxSize={6}
                                            color="green.500"
                                            aria-label="Selected"
                                        />
                                    )}
                                </HStack>

                                {/* Description */}
                                <Text color={textColor} fontSize="md">
                                    {type.description}
                                </Text>

                                {/* Features */}
                                <VStack align="stretch" spacing={2}>
                                    <Text fontWeight="bold" color={headingColor} fontSize="sm">
                                        Features:
                                    </Text>
                                    {type.features.map((feature, idx) => (
                                        <HStack key={idx} spacing={2}>
                                            <Icon as={FaCheckCircle} color="green.500" boxSize={4} />
                                            <Text fontSize="sm" color={textColor}>
                                                {feature}
                                            </Text>
                                        </HStack>
                                    ))}
                                </VStack>
                            </VStack>
                        </Box>
                    ))}
                </Grid>

                {/* Action Buttons */}
                <HStack spacing={4} justify="center" width="100%">
                    <Button
                        onClick={onBack}
                        variant="outline"
                        size="lg"
                        aria-label="Go back to previous screen"
                    >
                        Back to Course Screen
                    </Button>
                    <Button
                        onClick={handleContinue}
                        colorScheme="blue"
                        size="lg"
                        isDisabled={!selectedType}
                        aria-label={`Continue with ${selectedType === 'selection' ? 'Multiple Choice Quiz' : 'Coding Challenge'}`}
                    >
                        Start Selected Quiz
                    </Button>
                </HStack>

                {/* Screen Reader Instructions */}
                <Box
                    className="sr-only"
                    role="region"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {selectedType && (
                        <Text>
                            {selectedType === 'selection'
                                ? 'Multiple Choice Quiz selected. Press Enter to continue or use arrow keys to switch to Coding Challenge.'
                                : 'Coding Challenge selected. Press Enter to continue or use arrow keys to switch to Multiple Choice Quiz.'}
                        </Text>
                    )}
                </Box>
            </VStack>
        </Box>
    );
};
